import React, { useEffect, useRef, useState } from "react";

import { ChevronDown } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { useProducts } from "~/hooks/useProducts";
import { cn } from "~/lib/utils";
import ErrorState from "~/pages/clients/components/client-detail/purchase-order/components/ErrorState";
import LoadingState from "~/pages/clients/components/client-detail/purchase-order/components/LoadingState";
import PriceRangesInfo from "~/pages/clients/components/client-detail/purchase-order/components/PriceRangesInfo";
import ProductForm from "~/pages/clients/components/client-detail/purchase-order/components/ProductForm";
import { useProfileStore } from "~/store/profileStore";
import { OrderItem, Product } from "~/types/product";

// Debounce helper
function useDebounce(value: any, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

interface ProductSelectorProps {
  onAddProduct: (
    product: Product,
    quantity: number,
    unitPrice: number,
    suggestedPrice: number,
    hasPriceRanges: boolean,
    discountPercent: number
  ) => void;
  editingItem: OrderItem | null;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({
  onAddProduct,
  editingItem,
}) => {
  const { products, priceRanges, loading, error } = useProducts();
  const { role } = useProfileStore();
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [unitPrice, setUnitPrice] = useState<number | null>(null);
  const [productPriceRanges, setProductPriceRanges] = useState<
    Array<{ min: number; max: number; price: number }>
  >([]);
  const [suggestedPrice, setSuggestedPrice] = useState<number | undefined>(
    undefined
  );
  const [warningThresholdPrice, setWarningThresholdPrice] = useState<
    number | undefined
  >(undefined);
  const [discountPercent, setDiscountPercent] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // For debounce
  const debouncedQuantity = useDebounce(quantity, 300);

  // Filter products based on search term and seller visibility
  const filteredProducts = products
    .filter((product) => {
      // First filter by search term
      const matchesSearch = product.short_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      // For sellers, also filter by visibility
      if (role === "seller") {
        return matchesSearch && product.visible_to_sellers !== false;
      }

      // For non-sellers (admin, superadmin, etc.), show all products
      return matchesSearch;
    })
    .sort((a, b) => a.short_name.localeCompare(b.short_name));

  // Reset form when not editing
  useEffect(() => {
    if (!editingItem) {
      setSelectedProductId("");
      setSelectedProduct(null);
      setQuantity(1);
      setUnitPrice(null);
      setDiscountPercent(0);
      setSuggestedPrice(undefined);
      setWarningThresholdPrice(undefined);
      setSearchTerm("");
    }
  }, [editingItem]);

  // Set form values when editing an item
  useEffect(() => {
    if (editingItem) {
      setSelectedProductId(editingItem.product.id);
      setSelectedProduct(editingItem.product);
      setQuantity(editingItem.quantity);
      setUnitPrice(editingItem.unitPrice);
      setDiscountPercent(editingItem.discountPercent ?? 0);
      setSearchTerm(editingItem.product.short_name);
    }
  }, [editingItem]);

  // Update selected product when selectedProductId changes
  useEffect(() => {
    if (selectedProductId && products.length > 0) {
      const product = products.find((p) => p.id === selectedProductId) || null;
      setSelectedProduct(product);
      // Auto-apply product-level discount (only for new items, not when editing)
      if (product && !editingItem) {
        setDiscountPercent(product.discount_percent ?? 0);
      }
    } else {
      setSelectedProduct(null);
    }
  }, [selectedProductId, products]);

  // Update available price ranges when selected product changes
  useEffect(() => {
    if (selectedProductId && priceRanges.length > 0) {
      const filteredRanges = priceRanges
        .filter((range) => range.product_id === selectedProductId)
        .map((range) => ({
          min: range.min,
          max: range.max,
          price: range.price,
        }))
        .sort((a, b) => a.min - b.min);

      setProductPriceRanges(filteredRanges);

      // Only set default price if not editing and prices are available
      if (!editingItem && filteredRanges.length > 0) {
        // Set the default price based on quantity (use the first matching range)
        const relevantRange =
          filteredRanges.find(
            (range) => quantity >= range.min && quantity <= range.max
          ) || filteredRanges[0];
        setUnitPrice(relevantRange.price);
      }
    } else {
      setProductPriceRanges([]);
      if (!editingItem) {
        setUnitPrice(null);
      }
    }
  }, [selectedProductId, priceRanges, editingItem, quantity]);

  // Calculate suggestedPrice and warningThresholdPrice whenever product/quantity changes (debounced)
  useEffect(() => {
    if (selectedProductId && productPriceRanges.length > 0) {
      // Find the current range for the quantity
      const currentRangeIndex = productPriceRanges.findIndex(
        (range) =>
          debouncedQuantity >= range.min && debouncedQuantity <= range.max
      );

      if (currentRangeIndex >= 0) {
        // Set suggested price to current range price (for display)
        setSuggestedPrice(productPriceRanges[currentRangeIndex].price);

        // Set warning threshold to next range price (for warning logic)
        if (currentRangeIndex < productPriceRanges.length - 1) {
          setWarningThresholdPrice(
            productPriceRanges[currentRangeIndex + 1].price
          );
        } else {
          // If we're in the last range, use current range price for warning too
          setWarningThresholdPrice(productPriceRanges[currentRangeIndex].price);
        }
      } else {
        // Fallback to first range if no match found
        setSuggestedPrice(productPriceRanges[0].price);
        setWarningThresholdPrice(productPriceRanges[0].price);
      }
    } else {
      setSuggestedPrice(undefined);
      setWarningThresholdPrice(undefined);
    }
  }, [selectedProductId, productPriceRanges, debouncedQuantity]);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Open dropdown when typing
  useEffect(() => {
    if (searchTerm.length > 0 && !selectedProduct) {
      setIsDropdownOpen(true);
    } else if (selectedProduct && searchTerm === selectedProduct.short_name) {
      setIsDropdownOpen(false);
    }
  }, [searchTerm, selectedProduct]);

  const handleProductChange = (value: string) => {
    setSelectedProductId(value);
  };

  const handleProductSelect = (product: Product) => {
    setSelectedProductId(product.id);
    setSearchTerm(product.short_name);
    setIsDropdownOpen(false);
  };

  const handleInputFocus = () => {
    // Select all text if there's a selected product
    if (selectedProduct && inputRef.current) {
      inputRef.current.select();
    }

    // Only open dropdown if no product is selected or search term doesn't match selected product
    if (
      !selectedProduct ||
      (selectedProduct && searchTerm !== selectedProduct.short_name)
    ) {
      setIsDropdownOpen(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear selection if search term doesn't match selected product
    if (selectedProduct && selectedProduct.short_name !== value) {
      setSelectedProductId("");
      setIsDropdownOpen(true);
    } else if (!selectedProduct && value.length > 0) {
      setIsDropdownOpen(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      selectedProduct &&
      quantity > 0 &&
      unitPrice !== null &&
      unitPrice > 0
    ) {
      onAddProduct(
        selectedProduct,
        quantity,
        unitPrice,
        suggestedPrice || unitPrice, // Pass the suggested price to the parent component
        productPriceRanges.length > 0, // Pass whether product has price ranges
        discountPercent
      );

      // Reset form if not editing
      if (!editingItem) {
        setSelectedProductId("");
        setSelectedProduct(null);
        setQuantity(1);
        setUnitPrice(null);
        setDiscountPercent(0);
        setSuggestedPrice(undefined);
        setWarningThresholdPrice(undefined);
        setSearchTerm("");
        setIsDropdownOpen(false);
      }
    }
  };

  if (loading) {
    return <LoadingState message="Cargando productos disponibles..." />;
  }

  if (error || !products) {
    return (
      <ErrorState message="No se pudieron cargar los productos. Por favor, intente nuevamente." />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        <div>
          <label
            htmlFor="product"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Producto
          </label>
          <div className="relative" ref={dropdownRef}>
            <Input
              id="product"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder="Buscar producto..."
              className="w-full"
              ref={inputRef}
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronDown
                className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </div>

            {isDropdownOpen && (
              <div className="absolute left-0 z-[9999] mt-1 w-full min-w-[400px] origin-top rounded-md border border-gray-300 bg-white shadow-lg duration-200 ease-out animate-in fade-in-0 zoom-in-95 slide-in-from-top-2">
                <div
                  className="max-h-60 overflow-y-auto py-1"
                  style={{
                    maxHeight: "240px",
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="cursor-pointer border-b border-gray-100 px-4 py-3 text-sm last:border-b-0 hover:bg-gray-100"
                        onClick={() => handleProductSelect(product)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">
                            {product.short_name}
                          </span>
                          <span
                            className={cn(
                              "rounded px-2 py-0.5 text-xs font-medium",
                              product.stock === 0
                                ? "bg-red-100 text-red-700"
                                : "bg-green-100 text-green-700"
                            )}
                          >
                            Stock: {product.stock}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-center text-sm text-gray-500">
                      No se encontraron productos
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {selectedProduct && (
          <ProductForm
            quantity={quantity}
            setQuantity={setQuantity}
            unitPrice={unitPrice}
            setUnitPrice={setUnitPrice}
            discountPercent={discountPercent}
            suggestedPrice={suggestedPrice}
            warningThresholdPrice={warningThresholdPrice}
          />
        )}
      </div>

      {selectedProduct && productPriceRanges.length > 0 && (
        <PriceRangesInfo
          selectedProduct={selectedProduct}
          priceRanges={productPriceRanges}
        />
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={
            !selectedProduct || quantity <= 0 || !unitPrice || unitPrice <= 0
          }
        >
          {editingItem ? "Actualizar producto" : "Agregar producto"}
        </Button>
      </div>
    </form>
  );
};

export default ProductSelector;
